import React, { useCallback } from 'react'
import { Button } from 'react-bootstrap'
import { ChangePasswordSchema, ChangePasswordType } from '../schema/ChangePasswordSchema'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import AdminLayout from '../../../layouts/AdminLayout'
import { useChangePassword } from '../hooks/useChangePassword'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import RequireAuth from '../../../auth/middleware/RequireAuth'

const ChangePasswordPages: React.FC = () => {
    const { register, handleSubmit, formState: { errors }, setError, reset } = useForm<ChangePasswordType>({
        resolver: zodResolver(ChangePasswordSchema)
    })

    const { mutate, isPending } = useChangePassword()

    const queryClient = useQueryClient()

    const onSubmit = useCallback((data: ChangePasswordType) => {
        toast.loading('Changing password...', {
            id: 'change-password',
        })
        mutate(data, {
            onSuccess: async () => {
                await queryClient.refetchQueries();

                reset();

                toast.success('Password updated successfully', { id: 'change-password' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof ChangePasswordType, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to update password', { id: 'change-password' });
                    }
                } else {
                    toast.error((mutationError as any)?.response?.data?.message || 'Failed to update password', { id: 'change-password' });
                }
            }
        });
    }, [])

    return (
        <AdminLayout>
            <div className="container-fluid my-4" >
                <div className="card shadow-sm p-4">
                    <h3 className="mb-3">Change Password</h3>
                    <form onSubmit={handleSubmit(onSubmit)} className='mb-3'>
                        <div className="mb-3">
                            <label className="form-label">Old Password</label>
                            <input type="password" className={`form-control ${errors.old_password ? 'is-invalid' : ''}`} {...register('old_password')} placeholder="Enter your old password" />
                            {errors.old_password && <div className="invalid-feedback">{errors.old_password.message}</div>}
                        </div>
                        <div className='mb-3'>
                            <label className="form-label">New Password</label>
                            <input type="password" className={`form-control ${errors.new_password ? 'is-invalid' : ''}`} {...register('new_password')} placeholder='Enter your new password' />
                            {errors.new_password && <div className="invalid-feedback">{errors.new_password.message}</div>}
                        </div>
                        <div className='mb-3'>
                            <label className="form-label">Confirm Password</label>
                            <input type="password" className={`form-control ${errors.confirm_new_password ? 'is-invalid' : ''}`} {...register('confirm_new_password')} placeholder='Confirm your new password' />
                            {errors.confirm_new_password && <div className="invalid-feedback">{errors.confirm_new_password.message}</div>}
                        </div>
                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['update_profile']} asComponent>
                            <Button type="submit" variant="primary" className="mt-3" disabled={isPending}>
                                {isPending ? 'Changing password...' : 'Change Password'}
                            </Button>
                        </RequireAuth>
                    </form>
                </div>
            </div>
        </AdminLayout>
    )
}

export default ChangePasswordPages